import React, { useState, useEffect } from 'react';
import FormTabs from './FormTabs';
import PaymentModalView from './PaymentModalView';
import FormSecurityNotice from './FormSecurityNotice';
import { CrossIcon, ForwardIcon } from './Icons';

const ForwardForm = ({ domain, onClose, onSwitchToRent, onSwitchToBuy }) => {
    const [formData, setFormData] = useState({
        forwardingUrl: '',
        forwardingPeriod: 1
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

    // Handle closing the modal with Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // Reset state only if user switched to a completely different domain
    const prevDomainIdRef = React.useRef(domain?.id);
    useEffect(() => {
        if (prevDomainIdRef.current !== domain?.id) {
            prevDomainIdRef.current = domain?.id;
            setFormData({ forwardingUrl: '', forwardingPeriod: 1 });
            setShowPaymentOptions(false);
            setInvoiceData(null);
            setSubmitSuccess(false);
            setSubmitError('');
        }
    }, [domain?.id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const calculatePrice = () => {
        const days = parseInt(formData.forwardingPeriod);
        const basePrice = domain.pricefw || Math.ceil((domain.pricerent || Math.ceil((domain.pricebuy || 0) * 0.05)) / 30);
        
        // Progressive discount: domain.discount_fw is the max discount applied at 30 days (rounded to tenths)
        const maxDiscount = domain.discount_fw !== undefined && domain.discount_fw !== null ? Number(domain.discount_fw) : 0;
        const rawDiscountPct = maxDiscount > 0 ? ((days - 1) / 29) * maxDiscount : 0;
        const discountPercent = Math.round(rawDiscountPct * 10) / 10;
        const total = (basePrice * days) * (1 - discountPercent / 100);
        
        return Math.round(total * 100) / 100;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsGeneratingInvoice(true);
        setIsSubmitting(true);
        setSubmitError('');
        setSubmitSuccess(false);

        try {
            const userData = JSON.parse(localStorage.getItem('telegramUser') || '{}');
            const res = await fetch('/api/create-crypto-invoice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'forward',
                    domainId: domain?.id,
                    days: formData.forwardingPeriod,
                    destination: formData.forwardingUrl || null,
                    userId: userData?.id || 1
                })
            });
            const data = await res.json();
            if (data.success && data.invoice) {
                setInvoiceData(data.invoice);
                setShowPaymentOptions(true);
            } else {
                setSubmitError(data.error || 'Failed to generate forwarding invoice.');
            }
        } catch (error) {
            console.error('Error generating invoice:', error);
            setSubmitError('Failed to generate forwarding invoice. Please try again.');
        } finally {
            setIsGeneratingInvoice(false);
            setIsSubmitting(false);
        }
    };

    const basePrice = domain.pricefw || Math.ceil((domain.pricerent || Math.ceil((domain.pricebuy || 0) * 0.05)) / 30);
    const currentPrice = calculatePrice();

    const rentUntil = domain?.rent_until ? new Date(domain.rent_until) : null;
    const forwardUntil = domain?.forward_until ? new Date(domain.forward_until) : null;
    const now = new Date();
    
    let busyUntil = null;
    if (rentUntil && rentUntil > now) busyUntil = rentUntil;
    if (forwardUntil && forwardUntil > now) {
        if (!busyUntil || forwardUntil > busyUntil) busyUntil = forwardUntil;
    }

    const hasBuy = Boolean(domain?.pricebuy && Number(domain.pricebuy) > 0);
    const hasRent = Boolean(domain?.pricerent && Number(domain.pricerent) > 0);
    const canSwitchToBuy = hasBuy ? onSwitchToBuy : undefined;
    const canSwitchToRent = hasRent ? onSwitchToRent : undefined;

    return (
        <div className="relative max-w-lg md:max-w-xl w-full animate-modal-strict">
            {/* Floating Domain Title and Close button */}
            <div className="flex items-center justify-between gap-2.5 sm:gap-3 mb-2.5 px-1 sm:px-2">
                {domain?.name ? (
                    <div className="flex items-center gap-2 min-w-0 max-w-[calc(100%-48px)]">
                        <span className="text-[10px] sm:text-xs font-bold tracking-wider uppercase text-neutral-400 bg-white/[0.08] border border-white/[0.12] px-2 py-0.5 rounded-md shrink-0">Order</span>
                        <span className="text-base sm:text-xl font-bold font-display text-white truncate domain-spotlight-text">
                            {domain.name}
                        </span>
                    </div>
                ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-wider">
                        <ForwardIcon className="w-3.5 h-3.5" />
                        <span>Domain Forwarding</span>
                    </div>
                )}

                {onClose && (
                    <button 
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-neutral-900/90 hover:bg-neutral-800 text-neutral-400 hover:text-white border border-white/[0.14] hover:border-white/[0.3] flex items-center justify-center transition-all duration-200 active:scale-90 shadow-lg cursor-pointer group shrink-0"
                        title="Close"
                        aria-label="Close modal"
                    >
                        <CrossIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200 group-hover:rotate-90 group-hover:scale-110" />
                    </button>
                )}
            </div>

            {/* Bookmark tabs sticking out of the modal window */}
            <div className="relative z-30">
                <FormTabs 
                    activeTab="forward" 
                    onSwitchToBuy={canSwitchToBuy} 
                    onSwitchToRent={canSwitchToRent} 
                    onSwitchToForward={undefined} 
                />
            </div>

            {/* Ambient floating glow behind the modal */}
            <div className="absolute -inset-3 top-16 bg-gradient-to-b from-white/[0.08] via-emerald-500/[0.04] to-transparent rounded-[32px] blur-2xl -z-10 pointer-events-none opacity-80" />

            <div className="bg-neutral-950/95 backdrop-blur-3xl rounded-3xl border border-white/[0.16] ring-1 ring-white/[0.08] max-h-[85vh] sm:max-h-[82vh] overflow-y-auto modal-scrollbar shadow-[0_30px_90px_rgba(0,0,0,0.95),0_10px_30px_rgba(0,0,0,0.7),inset_0_1px_1px_rgba(255,255,255,0.22)] relative z-10">
                {/* Top specular edge reflection */}
                <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none rounded-t-3xl z-10" />

                <div className="p-5 sm:p-7 md:p-8">
                    
                    {busyUntil ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold text-white mb-2">Domain is Busy</h4>
                            <p className="text-neutral-400 mb-6">The domain <span className="text-primary-400 font-semibold">{domain?.name}</span> is currently occupied until {busyUntil.toLocaleDateString()}. Notify me when it becomes available?</p>
                            <a 
                                href={process.env.REACT_APP_TELEGRAM_CONTACT || (process.env.REACT_APP_TELEGRAM_BOT_USERNAME ? `https://t.me/${process.env.REACT_APP_TELEGRAM_BOT_USERNAME}?start=notify_${domain?.id}` : '#')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-6 py-2.5 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-xl transition-colors border border-white/[0.12]"
                            >
                                Notify me
                            </a>
                        </div>
                    ) : showPaymentOptions && invoiceData ? (
                        <PaymentModalView
                            domain={domain}
                            action="forward"
                            durationText={`${formData.forwardingPeriod} day(s) redirection`}
                            amount={invoiceData.amount}
                            invoiceData={{ ...invoiceData, duration: formData.forwardingPeriod }}
                            telegramBotUsername={process.env.REACT_APP_TELEGRAM_BOT_USERNAME || ''}
                            destination={formData.forwardingUrl}
                            onBack={() => { setShowPaymentOptions(false); setInvoiceData(null); }}
                        />
                    ) : submitSuccess ? (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2">Payment Successful!</h4>
                        <p className="text-neutral-400">We'll contact you with setup details.</p>
                    </div>
                ) : (
                    <>
                        <p className="text-neutral-300 mb-6">
                            Forward <span className="text-primary-400 font-semibold">{domain?.name}</span> to a specified URL.
                        </p>
                        
                        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
                            <div>
                                <div className="relative">
                                    <input 
                                        type="url" 
                                        id="forward_url"
                                        name="forwardingUrl"
                                        value={formData.forwardingUrl}
                                        onChange={handleChange}
                                        placeholder=" "
                                        required
                                        className="peer block w-full px-3.5 py-2.5 bg-neutral-900/90 border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 rounded-xl text-white focus:outline-none transition-all placeholder-transparent text-sm"
                                    />
                                    <label
                                        htmlFor="forward_url"
                                        className="absolute start-3 -top-2.5 z-10 px-1.5 text-xs text-neutral-400 bg-neutral-950 rounded pointer-events-none transition-all duration-200 transform origin-left peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-white peer-focus:bg-neutral-950"
                                    >
                                        Forwarding Destination URL (e.g. https://example.com)
                                    </label>
                                </div>
                                <p className="text-xs text-neutral-400 mt-1.5 px-1">
                                    The URL to which your domain will forward
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-neutral-300 mb-1">
                                    Forwarding Period: {formData.forwardingPeriod} day{formData.forwardingPeriod !== 1 ? 's' : ''}
                                </label>
                                <input 
                                    type="range" 
                                    name="forwardingPeriod"
                                    min="1"
                                    max="30"
                                    value={formData.forwardingPeriod}
                                    onChange={handleChange}
                                    className="w-full accent-white"
                                />
                                <div className="flex justify-between text-xs text-neutral-400 mt-1">
                                    <span>1 day</span>
                                    <span>30 days</span>
                                </div>
                                <div className="text-center mt-2">
                                    {formData.forwardingPeriod > 1 && domain.discount_fw > 0 && (
                                        <>
                                            <p className="text-xs text-neutral-500 line-through mb-1">
                                                ${(basePrice * formData.forwardingPeriod).toFixed(2)}
                                            </p>
                                            <p className="text-xs text-emerald-400">
                                                Progressive discount applied! ({(((formData.forwardingPeriod - 1) / 29) * Number(domain.discount_fw)).toFixed(1)}% off)
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            {submitError && typeof submitError === 'string' && (
                                <div className="text-red-400 text-sm text-center bg-red-400/10 p-3 rounded-xl mb-4">{submitError}</div>
                            )}
                            {submitError && typeof submitError === 'object' && (
                                <div className="text-orange-400 text-sm text-center bg-orange-400/10 p-4 rounded-xl mb-4">
                                    <p className="mb-2">{submitError.message}</p>
                                    <a 
                                        href={process.env.REACT_APP_TELEGRAM_CONTACT || (process.env.REACT_APP_TELEGRAM_BOT_USERNAME ? `https://t.me/${process.env.REACT_APP_TELEGRAM_BOT_USERNAME}?start=notify_${domain?.id}` : '#')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block mt-2 px-4 py-2 bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl transition-colors border border-white/[0.12]"
                                    >
                                        Notify me
                                    </a>
                                </div>
                            )}
                            
                            <button 
                                type="submit"
                                disabled={isSubmitting || isGeneratingInvoice}
                                className="w-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 hover:from-emerald-400 hover:via-emerald-500 hover:to-teal-500 text-white font-semibold py-3.5 px-5 rounded-xl transition-all duration-200 border border-emerald-400/30 hover:border-emerald-300/50 shadow-[0_10px_25px_-5px_rgba(16,185,129,0.35),0_4px_10px_rgba(0,0,0,0.4),inset_0_1px_1px_rgba(255,255,255,0.35)] active:scale-[0.99] flex items-center justify-center min-h-[52px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold"
                            >
                                {isGeneratingInvoice ? 'Creating Invoice...' : isSubmitting ? 'Processing...' : `Continue to Payment $${currentPrice.toFixed(2)}`}
                            </button>
                        </form>
                    </>
                )}
            </div>
            
            <FormSecurityNotice />
        </div>
        </div>
    );
};

export default ForwardForm;